/**
 * View-model + helpers for the test-set version-history UI.
 *
 * A version is a read-only snapshot of the test set's content (its test-case grid).
 * `buildVersionDisplay` maps a prior snapshot's rows; `buildCurrentDisplay` maps the live test
 * set's current rows. Both produce the same `VersionRow[]` the read-only grid renders, so the
 * history shows the test set as it was, not a separate config view.
 */

import type { GetTestSetVersionsResponse } from "@/types_api/backend/transport/get_test_set_versions_response"
import type { TestCaseRecord } from "@/types/api/TestCaseRecord"
import type { TestSetRecord } from "@/types/api/TestSetRecord"

import { getLatestUpdatedAt } from "../../utils/testSetEditorContextState"

export type TestSetVersionResource =
  GetTestSetVersionsResponse["versions"][number]

export const CURRENT_VERSION_KEY = "current"

/** One test-case row, normalized for the read-only grid. */
export interface VersionRow {
  key: string
  type: string
  simulationInput: string
  description: string
  expectedBehaviors: string[]
}

/**
 * View-model for a row in the version-history panel and the read-only grid it drives.
 * `isCurrent` marks the live test set (the default selection); the rest map a prior snapshot.
 */
interface VersionDisplay {
  key: string
  title: string
  subtitle: string
  isCurrent: boolean
  changeType?: "save" | "revert"
  rows: VersionRow[]
}

export function formatTimestamp(value: string | null | undefined): string {
  if (!value) {
    return "Unknown date"
  }
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) {
    return "Unknown date"
  }
  return parsed.toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  })
}

function asString(value: unknown): string {
  return typeof value === "string" ? value : ""
}

/**
 * Map a raw test-case row to the grid view-model. Works for both a prior version's snapshot row
 * (a plain JSON object) and a live TestCaseRecord, since both carry the same field names.
 */
function toVersionRow(raw: Record<string, unknown>, index: number): VersionRow {
  const id = asString(raw.id)
  const expectedBehaviors = Array.isArray(raw.expected_behaviors)
    ? raw.expected_behaviors.filter(
        (entry): entry is string => typeof entry === "string",
      )
    : []
  return {
    key: id || `row-${index}`,
    type: asString(raw.input_type),
    simulationInput: asString(raw.input_str),
    description: asString(raw.description),
    expectedBehaviors,
  }
}

export function buildCurrentDisplay(
  record: TestSetRecord,
  rows: TestCaseRecord[],
): VersionDisplay {
  return {
    key: CURRENT_VERSION_KEY,
    title: "Current version",
    subtitle: `Last edited ${formatTimestamp(
      getLatestUpdatedAt(record.last_updated_at || record.created_at, rows),
    )}`,
    isCurrent: true,
    rows: rows.map((row, index) =>
      toVersionRow(row as unknown as Record<string, unknown>, index),
    ),
  }
}

export function buildVersionDisplay(
  version: TestSetVersionResource,
): VersionDisplay {
  const snapshotRows = version.test_cases ?? []
  return {
    key: version.ulid,
    title: version.label
      ? `Version ${version.version_number} · ${version.label}`
      : `Version ${version.version_number}`,
    subtitle: `${version.created_by || "Unknown"} · ${formatTimestamp(version.created_at)}`,
    isCurrent: false,
    changeType: version.change_type,
    rows: snapshotRows.map((row, index) =>
      toVersionRow(row as Record<string, unknown>, index),
    ),
  }
}
